import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useEventListener } from '@vueuse/core'
import { abortActiveDrag } from '@/composables/useDragSelect'

/**
 * Desktop-style keyboard shortcuts.
 *
 * One window-level dispatcher (installed once in App.vue) routes keys to:
 * - commands: app-level actions registered by whichever component owns them
 *   (`project.save`, `data.delete`, `map.cancelDraw`, ...). Toolbar buttons call
 *   the same commands through `runCommand`.
 * - context handlers: Ctrl+A / Delete / Escape for the panel the user last
 *   pointed at or focused (`[data-shortcut-ctx]`).
 *
 * Must stay free of map imports: App.vue loads it before the mars3d runtime.
 */

export type ShortcutContext = 'data' | 'layers'

export interface ContextHandlers {
  selectAll?: () => void
  /** Ctrl/Cmd+Shift+A. */
  selectAllAssets?: () => void
  delete?: () => void
  /** Clear the selection; return true if there was anything to clear. */
  clear?: () => boolean
}

/** Returns true when it did something (only meaningful for Escape steps). */
type Command = () => boolean | void

export const platform = {
  mac:
    typeof navigator !== 'undefined' &&
    /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent),
}

/** Cmd on macOS, Ctrl elsewhere. Ctrl+Click on a Mac stays a context menu. */
export const modKey = (e: { metaKey: boolean; ctrlKey: boolean }) =>
  platform.mac ? e.metaKey : e.ctrlKey

/** Tooltip text for a shortcut, e.g. `Ctrl+Shift+S` or `⇧⌘S`. */
export const hint = (key: string, { shift = false }: { shift?: boolean } = {}) =>
  platform.mac ? `${shift ? '⇧' : ''}⌘${key}` : `Ctrl+${shift ? 'Shift+' : ''}${key}`

const NON_TEXT_INPUTS = new Set([
  'checkbox',
  'radio',
  'button',
  'submit',
  'reset',
  'range',
  'color',
  'file',
  'image',
])

export const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  if (target instanceof HTMLInputElement) return !NON_TEXT_INPUTS.has(target.type)
  return target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement
}

const OVERLAY_SELECTOR = [
  '.v-overlay--active.v-dialog',
  '.v-overlay--active.v-menu',
  '.ant-select-dropdown:not(.ant-select-dropdown-hidden)',
  '.ant-modal-wrap',
].join(',')

const isShown = (el: Element) => {
  const style = getComputedStyle(el)
  return style.display !== 'none' && style.visibility !== 'hidden'
}

/** A dialog, menu or dropdown is open (tooltips don't count). */
export const overlayOpen = (): boolean =>
  Array.from(document.querySelectorAll(OVERLAY_SELECTOR)).some(isShown)

const isInsideOverlay = (el: Element) =>
  !!el.closest('.v-overlay__content, .ant-select-dropdown, .ant-modal-root')

export const activeContext = ref<ShortcutContext | null>(null)
const contexts = new Map<ShortcutContext, ContextHandlers>()
const commands = new Map<string, Command>()

export const registerContextHandlers = (ctx: ShortcutContext, handlers: ContextHandlers) => {
  contexts.set(ctx, handlers)
  return () => {
    if (contexts.get(ctx) !== handlers) return
    contexts.delete(ctx)
    if (activeContext.value === ctx) activeContext.value = null
  }
}

export const registerCommand = (id: string, run: Command) => {
  commands.set(id, run)
  return () => {
    if (commands.get(id) === run) commands.delete(id)
  }
}

export const runCommand = (id: string): boolean => commands.get(id)?.() === true

/** Register context handlers for the lifetime of the calling component. */
export const useShortcutContext = (ctx: ShortcutContext, handlers: ContextHandlers) => {
  let off: (() => void) | undefined
  onMounted(() => (off = registerContextHandlers(ctx, handlers)))
  onBeforeUnmount(() => off?.())
}

/** Register a command for the lifetime of the calling component. */
export const useCommand = (id: string, run: Command) => {
  let off: (() => void) | undefined
  onMounted(() => (off = registerCommand(id, run)))
  onBeforeUnmount(() => off?.())
}

const consume = (e: Event) => {
  e.preventDefault()
  e.stopImmediatePropagation()
}

const onEscape = (e: KeyboardEvent) => {
  if (abortActiveDrag()) return consume(e)
  // Vuetify closes its own overlay on Escape; a persistent one stays open
  // and swallows the key here too.
  if (overlayOpen()) return
  if (runCommand('map.cancelDraw')) return consume(e)
  const handlers = activeContext.value && contexts.get(activeContext.value)
  if (handlers?.clear?.()) consume(e)
}

export const onShortcutKeyDown = (e: KeyboardEvent) => {
  if (e.defaultPrevented || e.isComposing) return
  const mod = modKey(e)
  const key = e.key.length === 1 ? e.key.toLowerCase() : e.key

  // Save works everywhere, even in text fields; the browser's "Save page"
  // dialog is never wanted inside the app.
  if (mod && !e.altKey && key === 's') {
    e.preventDefault()
    if (!e.repeat) runCommand(e.shiftKey ? 'project.saveAs' : 'project.save')
    return
  }
  if (isEditableTarget(e.target)) return
  if (key === 'Escape') return onEscape(e)
  if (overlayOpen()) return

  const handlers = activeContext.value && contexts.get(activeContext.value)
  if (!handlers) return
  const selectAll = e.shiftKey ? handlers.selectAllAssets : handlers.selectAll
  if (mod && !e.altKey && key === 'a' && selectAll) {
    consume(e)
    selectAll()
  } else if ((key === 'Delete' || key === 'Backspace') && !mod && !e.altKey && handlers.delete) {
    consume(e)
    if (!e.repeat) handlers.delete()
  }
}

const onContextPointer = (e: Event) => {
  const target = e.target
  if (!(target instanceof Element) || isInsideOverlay(target)) return
  const el = target.closest<HTMLElement>('[data-shortcut-ctx]')
  activeContext.value = (el?.dataset.shortcutCtx as ShortcutContext | undefined) ?? null
}

/** Install the window listeners; call once from App.vue setup. */
export const installShortcutDispatcher = () => {
  useEventListener(window, 'keydown', onShortcutKeyDown, { capture: true })
  useEventListener(window, 'pointerdown', onContextPointer, { capture: true })
  useEventListener(window, 'focusin', onContextPointer, { capture: true })
}
